/**
 * Pure helpers for the in-page WhatsApp QR pairing step.
 *
 * The pairing view is fetched from the API (which proxies the wa-bridge); the
 * browser never reaches the bridge and never receives a token. These helpers
 * carry no React and no I/O so the wizard's polling hook and its tests can share
 * one source of truth: which states are terminal, whether a QR is renderable,
 * and how the expiry countdown reads.
 */

export type PairingState =
  "awaiting_qr" | "qr_ready" | "pairing" | "connected" | "logged_out";

/** The strict pairing view the API returns. Never carries a session key. */
export interface PairingView {
  readonly state: PairingState;
  readonly qrDataUrl?: string;
  readonly expiresAt?: string;
  readonly pairingCode?: string;
}

export const PAIRING_POLL_INTERVAL_MS = 2_500;

const QR_DATA_URL_PREFIX = "data:image/png;base64,";
const MAX_QR_DATA_URL_LENGTH = 24_576;

const PAIRING_CODE_PATTERN = /^[A-Za-z0-9]{8}$/;
const PHONE_NUMBER_PATTERN = /^\d{8,15}$/;

/**
 * Defense-in-depth re-validation before a QR is bound to an <img>. The API
 * already enforces this, but the browser refuses to render anything that is not
 * a bounded inline PNG data URL, so a regressed or spoofed value can never
 * become an arbitrary image source.
 */
export function isRenderableQrDataUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value.length === 0 || value.length > MAX_QR_DATA_URL_LENGTH) return false;
  if (!value.startsWith(QR_DATA_URL_PREFIX)) return false;
  const body = value.slice(QR_DATA_URL_PREFIX.length);
  return body.length > 0 && /^[A-Za-z0-9+/]+={0,2}$/.test(body);
}

/** True once pairing has reached a state that ends polling. */
export function isTerminalPairingState(state: PairingState): boolean {
  return state === "connected" || state === "logged_out";
}

/**
 * Whole seconds until the code expires, clamped at zero, or null when the view
 * carries no expiry or an unparseable one. Never returns a negative value.
 */
export function secondsUntilExpiry(
  expiresAt: string | undefined,
  now: number,
): number | null {
  if (!expiresAt) return null;
  const expiry = Date.parse(expiresAt);
  if (!Number.isFinite(expiry)) return null;
  return Math.max(0, Math.ceil((expiry - now) / 1_000));
}

/** Formats a remaining-seconds count as m:ss for the countdown label. */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

/**
 * Strips everything except digits from a raw phone entry. The user may type a
 * leading "+", spaces or separators; the server accepts only country-coded
 * digits, so the wizard cleans the value before validating and sending it.
 */
export function cleanPhoneNumber(raw: string): string {
  return raw.replace(/\D+/gu, "");
}

/** True when a cleaned phone number is 8–15 digits (country-coded, E.164). */
export function isValidPhoneNumber(digits: string): boolean {
  return PHONE_NUMBER_PATTERN.test(digits);
}

/** A dial code the wizard offers as a preset; free entry covers the rest. */
export interface DialCode {
  readonly code: string;
  readonly name: string;
}

/** Common dial codes; Turkey first as the default. Not exhaustive by design. */
export const COMMON_DIAL_CODES: readonly DialCode[] = [
  { code: "90", name: "Türkiye" },
  { code: "1", name: "Amerika Birleşik Devletleri / Kanada" },
  { code: "44", name: "Birleşik Krallık" },
  { code: "49", name: "Almanya" },
  { code: "31", name: "Hollanda" },
  { code: "33", name: "Fransa" },
  { code: "971", name: "Birleşik Arap Emirlikleri" },
  { code: "966", name: "Suudi Arabistan" },
];

export const DEFAULT_DIAL_CODE = "90";

/**
 * Deterministically reduces a raw national-number entry to its national digits
 * (without the country code). No guessing: it strips separators, an "00"
 * international access prefix, a pasted copy of the selected country code, and
 * leading national-trunk zeros — in that fixed order — so "0532…", "+90 532…",
 * "0090532…" and "90532…" all reduce to the same national digits under +90.
 */
export function normalizeNationalNumber(
  countryCode: string,
  nationalRaw: string,
): string {
  const cc = cleanPhoneNumber(countryCode);
  let national = cleanPhoneNumber(nationalRaw);
  if (national.startsWith("00")) national = national.slice(2);
  if (cc.length > 0 && national.startsWith(cc)) {
    national = national.slice(cc.length);
  }
  return national.replace(/^0+/u, "");
}

/** The canonical E.164 digit string (country code + national), no "+". */
export function composePhoneNumber(
  countryCode: string,
  nationalRaw: string,
): string {
  return (
    cleanPhoneNumber(countryCode) +
    normalizeNationalNumber(countryCode, nationalRaw)
  );
}

/**
 * Human-readable preview of the number that will be sent: "+CC NNN NNN NN NN".
 * A ten-digit national number groups as 3-3-2-2; anything else groups in
 * threes. Preview only — the sent value is always the compact digit string.
 */
export function formatPhonePreview(
  countryCode: string,
  nationalNumber: string,
): string {
  const cc = cleanPhoneNumber(countryCode);
  const national = cleanPhoneNumber(nationalNumber);
  if (national.length === 0) return `+${cc}`;
  const grouped =
    national.length === 10
      ? `${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(
          6,
          8,
        )} ${national.slice(8, 10)}`
      : national.replace(/(\d{3})(?=\d)/gu, "$1 ");
  return `+${cc} ${grouped}`;
}

/** True when a value is the exact eight-character linking-code shape. */
export function isPairingCode(value: unknown): value is string {
  return typeof value === "string" && PAIRING_CODE_PATTERN.test(value);
}

/** Groups the eight-character code as "XXXX-XXXX" for readable display. */
export function formatPairingCode(code: string): string {
  return `${code.slice(0, 4)}-${code.slice(4, 8)}`;
}
